import { useEffect, useRef } from 'react'
import { X, Play, Heart, MessageCircle, Share2, ExternalLink, Calendar } from 'lucide-react'
import { platformColors, platformIcons, platformNames } from '../data/config'
import { getTheme, isLightColor } from '../utils/colors'

function WorkDetailModal({ work, isOpen, onClose, config }) {
  const modalRef = useRef(null)

  // 根据背景色判断主题
  const sectionBg = config.profile?.sectionBackground || '#111827'
  const isLight = isLightColor(sectionBg)
  const theme = getTheme(isLight, config.profile?.fontColor)

  // 格式化数字
  const formatNumber = (num) => {
    if (typeof num === 'string') return num
    if (num >= 10000) return (num / 10000).toFixed(1) + 'w'
    return num.toString()
  }

  // ESC 关闭弹窗
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden' // 防止背景滚动
    }
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // 点击背景关闭
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen || !work) return null

  const platformColor = platformColors[work.platform] || '#6b7280'
  const platformName = platformNames[work.platform] || work.platform
  const hasUrl = work.url && work.url !== '#'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl animate-fadeIn ${theme.cardBg} ${theme.cardBorder}`}
        style={{ backgroundColor: sectionBg }}
      >
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${isLight ? 'bg-black/20 text-gray-800 hover:bg-black/30' : 'bg-black/50 text-white hover:bg-black/70'}`}
        >
          <X className="w-5 h-5" />
        </button>

        {/* 封面图 */}
        <div className="relative aspect-[16/9] overflow-hidden rounded-t-2xl">
          <img
            src={work.cover}
            alt={work.title}
            className="w-full h-full object-cover"
          />
          {/* 平台标签 */}
          <div
            className="absolute top-4 left-4 px-3 py-1.5 rounded-lg text-sm font-medium text-white flex items-center gap-2"
            style={{ backgroundColor: platformColor }}
          >
            <span>{platformIcons[work.platform]}</span>
            {platformName}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="p-6 space-y-4">
          {/* 标题 */}
          <h2 className={`text-2xl font-bold pr-10 ${theme.text}`}>
            {work.title}
          </h2>

          {/* 发布日期 */}
          <div className={`flex items-center gap-2 text-sm ${theme.textSecondary}`}>
            <Calendar className="w-4 h-4" />
            <span>{work.publishDate || '未知日期'}</span>
          </div>

          {/* 描述 */}
          {work.description && (
            <p className={`leading-relaxed ${theme.textSecondary}`}>
              {work.description}
            </p>
          )}

          {/* 数据统计 */}
          <div className={`grid grid-cols-4 gap-4 py-4 border-t border-b ${theme.divider}`}>
            <div className="text-center">
              <div className={`flex items-center justify-center gap-1 mb-1 ${theme.textMuted}`}>
                <Play className="w-4 h-4" />
                <span className="text-xs">播放</span>
              </div>
              <div className={`text-lg font-bold ${theme.text}`}>
                {formatNumber(work.views)}
              </div>
            </div>
            <div className="text-center">
              <div className={`flex items-center justify-center gap-1 mb-1 ${theme.textMuted}`}>
                <Heart className="w-4 h-4" />
                <span className="text-xs">点赞</span>
              </div>
              <div className={`text-lg font-bold ${theme.text}`}>
                {formatNumber(work.likes)}
              </div>
            </div>
            <div className="text-center">
              <div className={`flex items-center justify-center gap-1 mb-1 ${theme.textMuted}`}>
                <MessageCircle className="w-4 h-4" />
                <span className="text-xs">评论</span>
              </div>
              <div className={`text-lg font-bold ${theme.text}`}>
                {formatNumber(work.comments)}
              </div>
            </div>
            <div className="text-center">
              <div className={`flex items-center justify-center gap-1 mb-1 ${theme.textMuted}`}>
                <Share2 className="w-4 h-4" />
                <span className="text-xs">分享</span>
              </div>
              <div className={`text-lg font-bold ${theme.text}`}>
                {formatNumber(work.shares)}
              </div>
            </div>
          </div>

          {/* 直达原作品按钮 */}
          {hasUrl ? (
            <a
              href={work.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl text-white font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: platformColor }}
            >
              <ExternalLink className="w-5 h-5" />
              <span>直达原作品 ({platformName})</span>
            </a>
          ) : (
            <div className={`w-full py-3 px-6 rounded-xl text-center ${theme.textMuted} ${isLight ? 'bg-gray-100' : 'bg-gray-800'}`}>
              暂无原作品链接
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WorkDetailModal
